import type { AbilityDef, AttackButton } from "../../sim";

/**
 * Дерево авто-комбо (Джон 04.11.2026, 22:50: «Я не хочу заучивать кнопки, я хочу, чтобы люди кайфовали, думая, что они
 * круто играют, а на самом деле сочетание клавиш и многократное нажатие даёт огромное количество продуманных комбо»).
 *
 * Каждый удар серии знает, куда ведёт каждая из четырёх кнопок; любая последовательность 2–5 нажатий идёт по веткам и
 * кончается финалом — сильным ударом со своими частицами. Узлы переиспользуются (дерево — сеть), поэтому путей —
 * десятки при двух десятках ударов. Предел длины серии — CHAIN_LIMIT в мире.
 */
const SPARK = { hit: "impacts/hit-spark", block: "block/spark" } as const;
const HEAVY = { hit: "heavy/spark", block: "block/spark" } as const;
const finaleFx = (hit: string): { readonly hit: string; readonly block: string } => ({ hit, block: "block/spark" });

type Node = Omit<AbilityDef, "input" | "branches"> & { readonly button: AttackButton };

const node = ({ button, ...ability }: Node): AbilityDef => ({ ...ability, input: { button, series: true } });

/** Удары серии — середина веток: быстрые, без длинного восстановления. */
const MID: readonly AbilityDef[] = [
  node({
    id: "lead-hook",
    name: "Боковой",
    button: "heavyPunch",
    pose: "hook",
    startup: 6,
    active: 3,
    recovery: 12,
    advance: 2,
    hitbox: { x: 20, y: -136, w: 46, h: 36 },
    hit: { damage: 45, hitstun: 16, blockstun: 10, hitstop: 8, push: 5, height: "mid" },
    fx: SPARK,
    sound: { hit: "hit-heavy" },
  }),
  node({
    id: "jab-kick",
    name: "Боковой ногой",
    button: "lightKick",
    pose: "lightKick",
    startup: 6,
    active: 3,
    recovery: 11,
    advance: 1,
    hitbox: { x: 20, y: -112, w: 66, h: 30 },
    hit: { damage: 40, hitstun: 15, blockstun: 9, hitstop: 7, push: 5, height: "mid" },
    fx: SPARK,
    sound: { hit: "hit-light" },
  }),
  node({
    id: "jab-high-kick",
    name: "Ногой в голову",
    button: "heavyKick",
    pose: "heavyKick",
    startup: 10,
    active: 4,
    recovery: 14,
    advance: 2,
    hitbox: { x: 24, y: -170, w: 76, h: 40 },
    hit: { damage: 60, hitstun: 18, blockstun: 12, hitstop: 9, push: 7, height: "high" },
    fx: HEAVY,
    sound: { hit: "hit-heavy" },
  }),
  node({
    id: "kick-cross",
    name: "Кросс",
    button: "lightPunch",
    pose: "cross",
    startup: 5,
    active: 3,
    recovery: 9,
    advance: 2.5,
    hitbox: { x: 22, y: -134, w: 50, h: 26 },
    hit: { damage: 35, hitstun: 14, blockstun: 9, hitstop: 6, push: 4, height: "high" },
    fx: SPARK,
    sound: { hit: "hit-light" },
  }),
  node({
    id: "kick-elbow",
    name: "Локоть с выпадом",
    button: "heavyPunch",
    pose: "elbowLunge",
    startup: 6,
    active: 3,
    recovery: 12,
    advance: 4,
    hitbox: { x: 20, y: -130, w: 60, h: 30 },
    hit: { damage: 50, hitstun: 17, blockstun: 10, hitstop: 8, push: 7, height: "mid" },
    fx: SPARK,
    sound: { hit: "hit-heavy" },
  }),
  node({
    id: "kick-high",
    name: "Ногой в голову",
    button: "heavyKick",
    pose: "heavyKick",
    startup: 10,
    active: 4,
    recovery: 14,
    advance: 2,
    hitbox: { x: 24, y: -170, w: 76, h: 40 },
    hit: { damage: 60, hitstun: 18, blockstun: 12, hitstop: 9, push: 7, height: "high" },
    fx: HEAVY,
    sound: { hit: "hit-heavy" },
  }),
  node({
    id: "hook-jab",
    name: "Джеб",
    button: "lightPunch",
    pose: "jab",
    startup: 4,
    active: 2,
    recovery: 8,
    advance: 1.5,
    hitbox: { x: 22, y: -132, w: 52, h: 26 },
    hit: { damage: 30, hitstun: 13, blockstun: 8, hitstop: 6, push: 4, height: "high" },
    fx: SPARK,
    sound: { hit: "hit-light" },
  }),
  node({
    id: "hook-kick",
    name: "Прямой ногой",
    button: "lightKick",
    pose: "frontKick",
    startup: 6,
    active: 4,
    recovery: 11,
    advance: 2.5,
    hitbox: { x: 20, y: -120, w: 72, h: 32 },
    hit: { damage: 45, hitstun: 16, blockstun: 10, hitstop: 7, push: 5, height: "mid" },
    fx: SPARK,
    sound: { hit: "hit-light" },
  }),
  node({
    id: "hook-high-kick",
    name: "Ногой в голову",
    button: "heavyKick",
    pose: "heavyKick",
    startup: 10,
    active: 4,
    recovery: 14,
    advance: 2,
    hitbox: { x: 24, y: -170, w: 76, h: 40 },
    hit: { damage: 60, hitstun: 18, blockstun: 12, hitstop: 9, push: 7, height: "high" },
    fx: HEAVY,
    sound: { hit: "hit-heavy" },
  }),
  node({
    id: "high-jab",
    name: "Кросс",
    button: "lightPunch",
    pose: "cross",
    startup: 5,
    active: 3,
    recovery: 9,
    advance: 2.5,
    hitbox: { x: 22, y: -134, w: 50, h: 26 },
    hit: { damage: 35, hitstun: 14, blockstun: 9, hitstop: 6, push: 4, height: "high" },
    fx: SPARK,
    sound: { hit: "hit-light" },
  }),
];

/** Финалы веток: сильный удар, свои частицы (огонь, гроза, золото, тьма), сбивает или подбрасывает. */
const FINALES: readonly AbilityDef[] = [
  node({
    id: "overhand-elbow",
    name: "Локоть сверху",
    button: "heavyPunch",
    pose: "overhandElbow",
    startup: 8,
    active: 4,
    recovery: 20,
    advance: 3,
    hitbox: { x: 16, y: -160, w: 58, h: 60 },
    hit: { damage: 85, hitstun: 0, blockstun: 16, hitstop: 12, push: 12, height: "high", knockdown: true, launch: 6 },
    fx: finaleFx("finale/fire"),
    sound: { start: "whoosh", hit: "hit-heavy" },
    finale: true,
  }),
  node({
    id: "hook-elbow",
    name: "Локоть снизу",
    button: "heavyPunch",
    pose: "risingElbow",
    startup: 7,
    active: 4,
    recovery: 18,
    hitbox: { x: 14, y: -170, w: 50, h: 90 },
    hit: { damage: 80, hitstun: 0, blockstun: 14, hitstop: 12, push: 4, height: "mid", launch: 15 },
    fx: finaleFx("finale/storm"),
    sound: { start: "whoosh", hit: "hit-heavy" },
    finale: true,
  }),
  node({
    id: "high-uppercut",
    name: "Апперкот",
    button: "heavyPunch",
    pose: "finisher",
    startup: 7,
    active: 5,
    recovery: 20,
    advance: 3,
    hitbox: { x: 14, y: -176, w: 54, h: 110 },
    hit: { damage: 85, hitstun: 0, blockstun: 16, hitstop: 12, push: 6, height: "mid", launch: 17 },
    fx: finaleFx("finale/gold"),
    sound: { start: "whoosh", hit: "hit-heavy" },
    finale: true,
  }),
  node({
    id: "high-sweep",
    name: "Подсечка",
    button: "lightKick",
    pose: "sweep",
    startup: 7,
    active: 4,
    recovery: 18,
    advance: 2,
    hitbox: { x: 10, y: -40, w: 90, h: 40 },
    hit: { damage: 75, hitstun: 0, blockstun: 14, hitstop: 12, push: 8, height: "low", knockdown: true },
    fx: finaleFx("finale/void"),
    sound: { start: "whoosh", hit: "hit-heavy" },
    finale: true,
  }),
];

/** Ветки: удар → кнопка → следующий удар. Финалы (и существующие добивания) дальше не ведут. */
export const COMBO_TREE: Readonly<Record<string, Readonly<Partial<Record<AttackButton, string>>>>> = {
  jab: { lightPunch: "cross", heavyPunch: "lead-hook", lightKick: "jab-kick", heavyKick: "jab-high-kick" },
  cross: { lightPunch: "body-hook", heavyPunch: "overhand-elbow", lightKick: "front-kick", heavyKick: "spin-kick" },
  "body-hook": {
    lightPunch: "series-finisher",
    heavyPunch: "hook-elbow",
    lightKick: "hurricane",
    heavyKick: "spin-kick",
  },
  "lead-hook": {
    lightPunch: "hook-jab",
    heavyPunch: "hook-elbow",
    lightKick: "hook-kick",
    heavyKick: "hook-high-kick",
  },
  "jab-kick": { lightPunch: "kick-cross", heavyPunch: "kick-elbow", lightKick: "front-kick", heavyKick: "spin-kick" },
  "jab-high-kick": {
    lightPunch: "high-jab",
    heavyPunch: "high-uppercut",
    lightKick: "high-sweep",
    heavyKick: "spin-kick",
  },
  hook: { lightPunch: "hook-jab", heavyPunch: "hook-elbow", lightKick: "hook-kick", heavyKick: "hook-high-kick" },
  "hook-jab": { lightPunch: "cross", heavyPunch: "overhand-elbow", lightKick: "front-kick", heavyKick: "kick-high" },
  "hook-kick": { lightPunch: "kick-cross", heavyPunch: "kick-elbow", lightKick: "hurricane", heavyKick: "spin-kick" },
  "hook-high-kick": {
    lightPunch: "high-jab",
    heavyPunch: "high-uppercut",
    lightKick: "high-sweep",
    heavyKick: "spin-kick",
  },
  "light-kick": { lightPunch: "kick-cross", heavyPunch: "kick-elbow", lightKick: "front-kick", heavyKick: "kick-high" },
  "front-kick": {
    lightPunch: "kick-cross",
    heavyPunch: "overhand-elbow",
    lightKick: "hurricane",
    heavyKick: "spin-kick",
  },
  "kick-cross": {
    lightPunch: "body-hook",
    heavyPunch: "high-uppercut",
    lightKick: "hurricane",
    heavyKick: "spin-kick",
  },
  "kick-elbow": { lightPunch: "hook-jab", heavyPunch: "hook-elbow", lightKick: "front-kick", heavyKick: "spin-kick" },
  "kick-high": { lightPunch: "high-jab", heavyPunch: "high-uppercut", lightKick: "high-sweep", heavyKick: "spin-kick" },
  "heavy-kick": {
    lightPunch: "high-jab",
    heavyPunch: "high-uppercut",
    lightKick: "high-sweep",
    heavyKick: "spin-kick",
  },
  "high-jab": {
    lightPunch: "body-hook",
    heavyPunch: "overhand-elbow",
    lightKick: "front-kick",
    heavyKick: "spin-kick",
  },
};

/** Удары, которые есть только в дереве (своих кнопок нет): середины веток и новые финалы. */
export const COMBO_NODES: readonly AbilityDef[] = [...MID, ...FINALES];

/** Прежние добивания серий — тоже финалы веток. */
export const FINALE_IDS: ReadonlySet<string> = new Set([
  "series-finisher",
  "hurricane",
  "spin-kick",
  ...FINALES.map((ability) => ability.id),
]);

/** Частицы финала для прежних добиваний: у каждого свой цвет. */
export const FINALE_FX: Readonly<Record<string, string>> = {
  "series-finisher": "finale/gold",
  hurricane: "finale/storm",
  "spin-kick": "finale/fire",
};

/** Ветки для экрана «Управление» — несколько для примера, остальные игрок открывает сам. */
export const COMBO_EXAMPLES: readonly (readonly AttackButton[])[] = [
  ["lightPunch", "lightPunch", "lightPunch", "lightPunch"],
  ["lightPunch", "lightPunch", "heavyPunch"],
  ["lightPunch", "lightKick", "heavyKick"],
  ["heavyPunch", "heavyKick", "heavyKick"],
  ["lightKick", "lightKick", "heavyPunch"],
];

/**
 * Серии при зажатом направлении. Зажато «назад» и жмёшь удары — отход с ударами: локоть с выпадом, кросс, хук,
 * добивание апперкотом или магией; «вниз» — низкая серия: тычок, низкий по ногам, апперкот из приседа, подсечка.
 * Одинаковых движений подряд нет. Ветка ведёт к финалу за 3–4 нажатия.
 */
const BACK_NODES: readonly AbilityDef[] = [
  node({
    id: "back-cross",
    name: "Кросс на отходе",
    button: "lightPunch",
    pose: "cross",
    startup: 5,
    active: 3,
    recovery: 9,
    advance: 3,
    // догнать отброшенного: короткий рывок к нему в начале удара (серия на отходе не бьёт в пустоту)
    actions: [{ frame: 1, type: "dash", params: { speed: 6 } }],
    hitbox: { x: 22, y: -134, w: 50, h: 26 },
    hit: { damage: 35, hitstun: 14, blockstun: 9, hitstop: 6, push: 5, height: "high" },
    fx: SPARK,
    sound: { hit: "hit-light" },
  }),
  node({
    id: "back-body-hook",
    name: "Хук на отходе",
    button: "lightPunch",
    pose: "bodyHook",
    startup: 6,
    active: 3,
    recovery: 10,
    advance: 3,
    // догнать отброшенного: короткий рывок к нему в начале удара (серия на отходе не бьёт в пустоту)
    actions: [{ frame: 1, type: "dash", params: { speed: 6 } }],
    hitbox: { x: 20, y: -130, w: 48, h: 34 },
    hit: { damage: 40, hitstun: 16, blockstun: 10, hitstop: 7, push: 5, height: "mid" },
    fx: SPARK,
    sound: { hit: "hit-light" },
  }),
  node({
    id: "back-kick",
    name: "Прямой ногой на отходе",
    button: "lightKick",
    pose: "frontKick",
    startup: 6,
    active: 4,
    recovery: 11,
    advance: 3,
    // догнать отброшенного: короткий рывок к нему в начале удара (серия на отходе не бьёт в пустоту)
    actions: [{ frame: 1, type: "dash", params: { speed: 6 } }],
    hitbox: { x: 20, y: -120, w: 72, h: 32 },
    hit: { damage: 45, hitstun: 16, blockstun: 10, hitstop: 7, push: 7, height: "mid" },
    fx: SPARK,
    sound: { hit: "hit-light" },
  }),
];

const LOW_NODES: readonly AbilityDef[] = [
  node({
    id: "low-kick-series",
    name: "Низкий по ногам",
    button: "lightPunch",
    pose: "legSweep",
    startup: 7,
    active: 4,
    recovery: 12,
    advance: 1,
    hitbox: { x: 8, y: -38, w: 92, h: 38 },
    hit: { damage: 40, hitstun: 16, blockstun: 9, hitstop: 7, push: 4, height: "low" },
    fx: SPARK,
    sound: { hit: "hit-light" },
  }),
  node({
    id: "low-uppercut",
    name: "Апперкот из приседа",
    button: "lightPunch",
    pose: "uppercut",
    startup: 7,
    active: 4,
    recovery: 14,
    advance: 2,
    hitbox: { x: 14, y: -170, w: 50, h: 120 },
    hit: { damage: 60, hitstun: 18, blockstun: 12, hitstop: 9, push: 4, height: "mid" },
    fx: HEAVY,
    sound: { hit: "hit-heavy" },
  }),
  node({
    id: "low-sweep-finale",
    name: "Подсечка",
    button: "lightPunch",
    pose: "sweep",
    startup: 7,
    active: 5,
    recovery: 18,
    advance: 3,
    hitbox: { x: 10, y: -40, w: 96, h: 40 },
    hit: { damage: 75, hitstun: 0, blockstun: 14, hitstop: 12, push: 8, height: "low", knockdown: true },
    fx: finaleFx("finale/void"),
    sound: { start: "whoosh", hit: "hit-heavy" },
    finale: true,
  }),
];

export const DIRECTION_NODES: readonly AbilityDef[] = [...BACK_NODES, ...LOW_NODES];

/** Ветки при зажатом «назад»: от локтя с выпадом (← □) и выстрела (← △) — к финалу. */
export const BACK_TREE: Readonly<Record<string, Readonly<Partial<Record<AttackButton, string>>>>> = {
  "elbow-lunge": { lightPunch: "back-cross", heavyPunch: "magic-shot", lightKick: "back-kick", heavyKick: "spin-kick" },
  "back-cross": {
    lightPunch: "back-body-hook",
    heavyPunch: "hook-elbow",
    lightKick: "back-kick",
    heavyKick: "spin-kick",
  },
  "back-body-hook": {
    lightPunch: "high-uppercut",
    heavyPunch: "magic-shot",
    lightKick: "hurricane",
    heavyKick: "spin-kick",
  },
  "back-kick": {
    lightPunch: "back-body-hook",
    heavyPunch: "hook-elbow",
    lightKick: "hurricane",
    heavyKick: "spin-kick",
  },
};

/** Ветки при зажатом «вниз»: от тычка из приседа (↓ □) и низкого по ногам (↓ ✕) — к подсечке или подбросу. */
export const DOWN_TREE: Readonly<Record<string, Readonly<Partial<Record<AttackButton, string>>>>> = {
  "crouch-jab": {
    lightPunch: "low-kick-series",
    heavyPunch: "low-uppercut",
    lightKick: "low-kick-series",
    heavyKick: "low-sweep-finale",
  },
  "crouch-kick": {
    lightPunch: "low-uppercut",
    heavyPunch: "low-uppercut",
    lightKick: "low-sweep-finale",
    heavyKick: "low-sweep-finale",
  },
  "low-kick-series": {
    lightPunch: "low-uppercut",
    // ↓ □ □ △ — локоть снизу вверх: был без ветки и не выходил ни у кого (тест путей бота, 05.11.2026)
    heavyPunch: "rising-elbow",
    lightKick: "low-sweep-finale",
    heavyKick: "low-sweep-finale",
  },
  "low-uppercut": {
    lightPunch: "low-sweep-finale",
    heavyPunch: "high-uppercut",
    lightKick: "low-sweep-finale",
    heavyKick: "low-sweep-finale",
  },
};
